import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom';

import { DndContext, DragEndEvent } from '@dnd-kit/core';
import styled from 'styled-components';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { useLocalStorageState } from '@components/component-helpers';
import { ErrorScreen } from '@components/error-screen';
import { useConfirmModal } from '@components/generic-modals/confirm-modal';
import { RouterTabsHeader } from '@components/header/router-tabs-header';
import { IconButton } from '@components/icon-button';
import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconAttributeJourney } from '@components/icons/attribute-journey';
import { IconBuilder } from '@components/icons/builder';
import { IconCloseRegular } from '@components/icons/close-regular';
import { IconEdit } from '@components/icons/edit';
import { IconFormIndicatorSuccess } from '@components/icons/form-indicator-success';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconMove } from '@components/icons/move';
import { IconPublish } from '@components/icons/publish';
import { IconSettings } from '@components/icons/settings';
import { IconTextFormatLinkOff } from '@components/icons/text-format-link-off';
import { IconTrash } from '@components/icons/trash';
import {
  IntegrationEndpointMinimal,
  IntegrationWebhookMinimal,
} from '@components/integrations/integrations-types';
import { SizedRelativeSpinner } from '@components/loading-spinner/loading-spinner';
import { Menu } from '@components/menu';
import { hideModal } from '@components/modal';
import { useRotatingLoadingMessages } from '@components/rotating-loading-messages';
import { hideSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { TextStatusIndicator } from '@components/text-status-indicator';
import { showError, useToaster } from '@components/toaster';
import { useNavigationBlock } from '@hooks/use-navigation-block';
import { getScopedTranslate, useScopedTranslate } from '@locale/translate';
import { storage } from '@utils/browser';
import { abbreviateDate } from '@utils/date';
import { minutes } from '@utils/milliseconds';

import {
  useJourneyIntegrationEndpoints,
  useJourneyIntegrationWebhook,
} from 'api/integrations/hooks';
import { useJourneyActionForms } from 'api/journey-action-forms/hooks';
import {
  useJourneyActionAvailableMoves,
  useJourneyActions,
  useMoveJourneyAction,
} from 'api/journey-actions/hooks';
import { listBreakingReferencesByMove } from 'api/journey-actions/requests';
import { useJourneyFormsList } from 'api/journey-forms/hooks';
import { useJourneyPollingConfigsList } from 'api/journey-polling/hooks';
import { useJourneyWebhooksList } from 'api/journey-webhooks/hooks';
import {
  useCreateJourney,
  useEditJourney,
  useJourney,
  useJourneyIssues,
  usePublishJourney,
} from 'api/journeys/hooks';
import {
  useChangeModifiedTravellerJourneys,
  useGetModifiedTravellerJourneys,
} from 'api/traveller-journeys/hooks';
import { BASE_ACTION_TYPE, JourneyAction, JourneyType } from 'api/types';
import { useEditJourneyActionFormOverlay } from 'ui/actions/combo-send-form/edit-journey-action-form-overlay';
import { useTriggersCollapsedStore } from 'ui/actions/triggers/common';
import { useEditJourneyFormOverlay } from 'ui/actions/triggers/journey-form/edit-journey-form-overlay';
import { useAddTravellersSidebar } from 'ui/actions/triggers/sidebars/add-trigger-sidebar';
import { TestIdContext, getShortIdToDescriptiveMap } from 'ui/authenticated/main/helpers';
import { routePaths } from 'ui/authenticated/navigation';
import { showActionSidebar } from 'ui/journey-builder/action-sidebar';
import { JourneyContext, journeyActionFormContextMapType } from 'ui/journey-builder/base-builder';
import { showCreateActionSidebar } from 'ui/journey-builder/create-action/create-action-sidebar';
import { MoveContext, hiddenIdentifier } from 'ui/journey-builder/edge';
import {
  CollapseContext,
  getNodesToHide,
  useCollapsedActions,
} from 'ui/journey-builder/hooks/use-collapsed-nodes';
import { generateEdgeId, useNodes } from 'ui/journey-builder/hooks/use-nodes';
import { JourneyBuilder } from 'ui/journey-builder/journey-builder';
import { useCancelChangesModal } from 'ui/journey-builder/modals/cancel-changes';
import { nodeTypes } from 'ui/journey-builder/node-types/';
import { GenericActionCard, nodeToActionBasic } from 'ui/journey-builder/node-types/common';
import { JourneyNode } from 'ui/journey-builder/node-types/types';
import { JourneyPresenceIndicator } from 'ui/journey-builder/presence/journey-presence-indicator';
import { PresenceProvider } from 'ui/journey-builder/presence/presence-context';

import { JourneyBreadcrumb } from './journey-breadcrumb';
import { JourneyEditContext, useJourneyEditContext } from './journey-edit-context';
import { useJourneyDetailsSidebar } from './journey-edit-sidebar';
import { JourneyFields } from './journey-fields';
import { JourneySettings } from './journey-settings';
import { useDeleteJourneyModal } from './modals/delete-journey';
import { usePublishAddTravellerActions } from './modals/publish-on-traveller-modal';
import { useIssuesSidebar } from './sidebars/issues';

const getPublishingKey = (id: string | undefined) => `journey-publishing-${id ?? ''}`;

const getIsPublishingInTheBackground = (journeyId: string | undefined) => {
  const key = getPublishingKey(journeyId);
  const value = storage.get(key);
  if (!value) return false;
  const timestamp = Number(value);
  if (Number.isNaN(timestamp)) {
    storage.remove(key);
    return false;
  }
  const twoMinutes = minutes(2);
  const hasRunLessThanTwoMinutes = Date.now() - timestamp < twoMinutes;
  if (!hasRunLessThanTwoMinutes) {
    storage.remove(key);
  }
  return hasRunLessThanTwoMinutes;
};

export const JourneyEdit = () => {
  const { t } = useScopedTranslate(locale);
  const { journeyId = '', workspaceId } = useParams();
  const navigate = useNavigate();
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const journeyQuery = useJourney(journeyId, {
    onError: () => {
      showError(t('journeyNotFound'));
      navigate('/');
    },
  });

  const journey = journeyQuery.data;
  const publishMutation = usePublishJourney(journeyId);
  const isPublishingInTheBackground = getIsPublishingInTheBackground(journeyId);
  useRotatingLoadingMessages({
    isEnabled:
      (publishMutation.isLoading || isPublishingInTheBackground) &&
      (journey ? journey.hasUnpublishedChanges : true),
    firstMessage: t('publishing'),
  });

  const integrationQuery = useJourneyIntegrationEndpoints(journeyId ?? '');
  const integrations = integrationQuery.data ?? [];
  const webhookQuery = useJourneyIntegrationWebhook(journeyId ?? '');
  const webhooks = webhookQuery.data ?? [];

  const showEditSidebar = useJourneyDetailsSidebar();
  const createMutation = useCreateJourney();
  const updateMutation = useEditJourney();

  useEffect(() => {
    const createJourney = async () => {
      const data = await createMutation.mutateAsync({
        title: t('defaultTitle'),
        workspace: workspaceId!,
      });
      navigate(
        generatePath(routePaths.editJourney, {
          workspaceId: workspaceId!,
          journeyId: data.shortId,
          '*': '',
        }),
        {
          replace: true,
        }
      );
    };
    if (!journeyId) {
      createJourney();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isLoading = journeyQuery.isLoading || integrationQuery.isLoading || webhookQuery.isLoading;

  if (!journey) return null;

  const saveJourneyTitle = async (title: string) => {
    if (!title || title === journey.title) return;
    await updateMutation.mutateAsync({ journeyId, title });
  };

  const tabs = [
    {
      label: t('journey'),
      section: 'journey',
      icon: <IconBuilder />,
      content: <JourneyFlow journey={journey} integrations={integrations} webhooks={webhooks} />,
    },
    {
      label: t('fields'),
      section: 'attributes',
      icon: <IconAttributeJourney />,
      content: (
        <JourneyFields
          journeyShortId={journeyId}
          attributes={journeyQuery.data?.attributes ?? []}
        />
      ),
    },
    {
      label: t('settings'),
      section: 'settings',
      icon: <IconSettings />,
      content: <JourneySettings journeyTimezone={journeyQuery.data?.timezone as string} />,
    },
  ];

  return (
    <PresenceProvider journeyId={journeyId}>
      <JourneyEditContext.Provider value={{ isEditingTitle, publishMutation }}>
        <RouterTabsHeader
          tabs={tabs}
          actions={<JourneyActions journey={journey} />}
          breadcrumbs={
            <JourneyBreadcrumb
              journey={journey}
              workspaceId={journey.workspace}
              onRename={saveJourneyTitle}
              onIsEditingChange={setIsEditingTitle}
              showEditSidebar={() => showEditSidebar({ journey, isEditing: true, workspaceId })}
              isBuilder
            />
          }
          isLoading={isLoading}
          backAction={
            <IconButton
              data-cy="journey-back-btn"
              icon={<IconArrowLeft />}
              variant="ghost-purple"
              onClick={() =>
                navigate(
                  generatePath(routePaths.journeyDetails, {
                    workspaceId: journey.workspace,
                    journeyId: journey.shortId,
                    '*': null,
                  })
                )
              }
            />
          }
        />
      </JourneyEditContext.Provider>
    </PresenceProvider>
  );
};

type JourneyProps = { journey: JourneyType };
type JourneyPublishActionProps = JourneyProps & { isDeleting: boolean };

const JourneyPublishAction = ({ journey, isDeleting }: JourneyPublishActionProps) => {
  const { publishMutation } = useJourneyEditContext();
  const cancelModal = useCancelChangesModal();
  const showIssuesSidebar = useIssuesSidebar();
  const issuesQuery = useJourneyIssues(journey.shortId);
  const { t } = useScopedTranslate(locale);
  const modifiedQuery = useGetModifiedTravellerJourneys(journey.shortId);
  const changeTravellers = useChangeModifiedTravellerJourneys();
  const navigate = useNavigate();
  const showModal = usePublishAddTravellerActions();
  const publishingKey = getPublishingKey(journey.shortId);
  const isPublishingInTheBackground = getIsPublishingInTheBackground(journey.shortId);
  const isPublishing = publishMutation.isLoading || isPublishingInTheBackground;
  const cannotPublish = isPublishing || !journey.isConfigured;

  const [hidePrompt, setHidePrompt] = useLocalStorageState<boolean>({
    key: `journey-publish-prompt-${journey.shortId}`,
    defaultValue: false,
  });

  const issues = issuesQuery.data ?? [];
  const hasIssues = issues.length > 0;
  const hasModifiedTravellers = modifiedQuery.data && modifiedQuery.data?.length > 0;

  const publishJourney = async () => {
    await publishMutation.mutateAsync(undefined);
    if (hasModifiedTravellers) {
      showModal({
        travellers: modifiedQuery.data ?? [],
        onUpdate: (ids: string[]) => {
          changeTravellers.mutateAsync(ids);
          hideModal();
        },
      });
    }
  };

  const runPublishInTheBackground = (ids?: string[]) => {
    storage.set(publishingKey, String(Date.now()));
    publishMutation.mutate(ids && ids.length > 0 ? { changeTravellerJourneyIds: ids } : undefined, {
      onSettled: () => {
        storage.remove(publishingKey);
      },
    });
  };

  useEffect(() => {
    if (!journey.hasUnpublishedChanges) {
      storage.remove(publishingKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journey.hasUnpublishedChanges]);

  useNavigationBlock({
    shouldBlock: journey.hasUnpublishedChanges && !hidePrompt && !isPublishing && !isDeleting,
    blockBeforeUnload: false,
    onSave: () =>
      new Promise<void>((resolve) => {
        const travellers = modifiedQuery.data ?? [];
        if (travellers.length > 0) {
          setTimeout(
            () =>
              showModal({
                travellers,
                onUpdate: (ids: string[]) => {
                  runPublishInTheBackground(ids);
                  resolve();
                },
                cancelButtonText: t('keepAsIs'),
                onCancel: () => {
                  runPublishInTheBackground(undefined);
                  resolve();
                },
              }),
            0
          );
          return;
        }
        runPublishInTheBackground(undefined);
        resolve();
      }),
    onDiscard: () =>
      navigate(
        generatePath(routePaths.journeyDetails, {
          workspaceId: journey.workspace,
          journeyId: journey.shortId,
          '*': null,
        })
      ),
    isSaveDisabled: cannotPublish,
    title: t('publishChanges'),
    titleIcon: <IconFormIndicatorWarning />,
    description: t('publishChangesDescription'),
    saveText: t('publishJourney'),
    discardText: t('dontPublish'),
    allowedPaths: ['/journeys/{id}/edit', '/journeys/{id}/edit/.*'],
    children: (
      <Checkbox label={t('dontShowAgain')} defaultValue={hidePrompt} onChange={setHidePrompt} />
    ),
  });

  if (!journey.hasUnpublishedChanges) {
    return null;
  }

  return (
    <Stack space={2} orientation="horizontal">
      <Button
        text={hasIssues ? t('issues', { num_issues: issues.length }) : t('noIssues')}
        variant="secondary"
        iconLeft={
          issuesQuery.isLoading ? (
            <SizedRelativeSpinner size={24} $skipMargin />
          ) : hasIssues ? (
            <IconFormIndicatorWarning />
          ) : (
            <IconFormIndicatorSuccess />
          )
        }
        onClick={() =>
          showIssuesSidebar({
            issues,
            numAttributes: journey.attributes.length,
          })
        }
      />
      {journey.hasPublishedJourney && (
        <Button
          variant="secondary"
          disabled={publishMutation.isLoading}
          onClick={() => cancelModal({ journeyShortId: journey.shortId })}
          text={t('cancelChanges')}
        />
      )}
      <Button
        disabled={cannotPublish}
        data-cy="publish"
        variant="primary"
        iconLeft={<IconPublish />}
        onClick={() => publishJourney()}
        tooltip={!journey.isConfigured ? t('journeyNotConfigured') : undefined}
        text={journey.hasPublishedJourney ? t('publishChanges') : t('publish')}
      />
    </Stack>
  );
};

const JourneyActions = ({ journey }: JourneyProps) => {
  const { t } = useScopedTranslate(locale);
  const showEditSidebar = useJourneyDetailsSidebar();
  const navigate = useNavigate();
  const showDeleteJourneyModal = useDeleteJourneyModal();
  const [isDeleting, setIsDeleting] = useState(false);
  const currentYear = new Date().getFullYear();
  const datePublished = new Date(journey.datePublished || '');

  if (!journey.shortId) {
    return (
      <Stack space={2}>
        <SizedRelativeSpinner size={24} />
      </Stack>
    );
  }

  return (
    <Stack space={2}>
      <Stack space={2} orientation="horizontal" justify="flex-end">
        <JourneyPublishAction journey={journey} isDeleting={isDeleting} />
        <Stack space={2} orientation="horizontal">
          <Menu
            dataTestid="journey-actions-btn"
            buttonProps={{
              variant: 'secondary',
              iconRight: <IconMoreVertical />,
            }}
          >
            <Menu.Item
              onSelect={() => showEditSidebar({ journey, isEditing: true })}
              icon={<IconEdit />}
              label={t('editJourneyDetails')}
            />
            <Menu.Item
              onSelect={() =>
                showDeleteJourneyModal({ journey, onDelete: () => setIsDeleting(true) })
              }
              dataCy="delete-journey"
              variant="danger"
              label={t('deleteJourney')}
              icon={<IconTrash />}
            />
          </Menu>
          <IconButton
            icon={<IconCloseRegular />}
            onClick={() => {
              navigate(
                generatePath(routePaths.journeyDetails, {
                  workspaceId: journey.workspace,
                  journeyId: journey.shortId,
                  '*': null,
                })
              );
            }}
          />
        </Stack>
      </Stack>
      <Stack orientation="horizontal" space={4} align="center" justify="flex-end">
        {journey.hasUnpublishedChanges ? (
          <TextStatusIndicator variant="info" text={t('draft')} />
        ) : (
          <Stack orientation="horizontal" space={2} align="center">
            <TextStatusIndicator variant="success" text={t('published')} />
            {journey.datePublished && (
              <Text
                color="textHelper"
                text={abbreviateDate(
                  datePublished,
                  'en',
                  datePublished?.getFullYear() === currentYear ? 'datetime-short' : 'datetime'
                )}
              />
            )}
          </Stack>
        )}
        <JourneyPresenceIndicator />
      </Stack>
    </Stack>
  );
};

const getDescription = (action: JourneyAction) => {
  const { t } = getScopedTranslate(locale);
  if (!action.isConfigured) {
    return t('configureAction');
  } else if (action.description) {
    return action.description;
  } else {
    const node = nodeTypes[action.action];
    return (
      (node &&
        'getDescription' in node &&
        typeof node.getDescription === 'function' &&
        node.getDescription(action)) ||
      t('noDescription')
    );
  }
};

const generateDescendantMaps = (actions: Array<JourneyAction>) => {
  const childrenMap: Record<(typeof actions)[number]['shortId'], JourneyAction[]> = {};

  actions.forEach((action) => {
    const parentId = action.parentAction?.shortId;
    if (parentId) {
      if (!childrenMap[parentId]) {
        childrenMap[parentId] = [];
      }
      childrenMap[parentId]!.push(action);
    }
    if (!childrenMap[action.shortId]) {
      childrenMap[action.shortId] = [];
    }
  });

  const descendantsCountMap: Record<(typeof actions)[number]['shortId'], number> = {};

  const countDescendants = (action: JourneyAction): number => {
    if (descendantsCountMap[action.shortId] !== undefined) {
      return descendantsCountMap[action.shortId];
    }

    const children = childrenMap[action.shortId] || [];

    let count = 0;
    children.forEach((childAction) => {
      const childCountValue = childAction.action === 'branch_base' ? 0 : 1;
      count += childCountValue + countDescendants(childAction);
    });

    descendantsCountMap[action.shortId] = count;

    return descendantsCountMap[action.shortId];
  };

  actions.forEach((action) => {
    countDescendants(action);
  });

  return { descendantsCountMap, childrenMap };
};

type ConvertToNodesProps = {
  actions: JourneyAction[];
  integrations: IntegrationEndpointMinimal[];
  webhooks: IntegrationWebhookMinimal[];
  collapsedActions: Array<JourneyAction['shortId']>;
  triggersCount: number;
  isTriggersCollapsed: boolean;
};

const getIntegrationEndpoint = (
  integrations: IntegrationEndpointMinimal[],
  action: JourneyAction<'integration'>
) => {
  const endpointId = action.values?.integrationEndpointId?.value ?? '';
  return integrations.find((integration) => integration.shortId === endpointId);
};
const getIntegrationWebhook = (
  webhooks: IntegrationWebhookMinimal[],
  action: JourneyAction<'webhook'>
) => {
  const webhookId = action.values?.integrationWebhookId?.value ?? '';
  return webhooks.find((integration) => integration.shortId === webhookId);
};

export const convertToNodes = ({
  actions,
  integrations,
  webhooks,
  collapsedActions,
  triggersCount,
  isTriggersCollapsed,
}: ConvertToNodesProps): JourneyNode[] => {
  const { t } = getScopedTranslate(locale);
  const nodes: JourneyNode[] = [];
  const nodesToHide = getNodesToHide({ actions, collapsedActions });
  const { descendantsCountMap, childrenMap } = generateDescendantMaps(actions);

  const getIsRemoveDisabled = (action: JourneyAction) => {
    if (action.action === 'journey_branch') {
      if (action.dependants.length > 1) {
        return true;
      }
      const children = childrenMap[action.shortId];
      if (children.length === 1) {
        return false;
      }
      const childrenWithChildren = [];
      children.forEach(({ shortId }) => {
        const childrensChildren = childrenMap[shortId];
        if (childrensChildren.length > 0) {
          childrenWithChildren.push(shortId);
        }
      });
      return childrenWithChildren.length > 1;
    }
    return false;
  };

  const journeyTriggersId = 'journey-triggers';

  if (actions.length > 0) {
    nodes.push({
      id: journeyTriggersId,
      type: 'journey_triggers',
      hidden: false,
      height: 700,
      dependantIds: [],
      data: {
        nodeDescription: t('journeyTriggers'),
        isConfigured: true,
        isCollapsed: false,
        values: { triggersCount, isTriggersCollapsed },
      },
    });
  }

  actions.forEach((action) => {
    const values = action.values ?? {};
    const nodeDescription = getDescription(action);
    const integration =
      action.action === 'integration'
        ? getIntegrationEndpoint(integrations, action as JourneyAction<'integration'>)
        : action.action === 'webhook'
        ? getIntegrationWebhook(webhooks, action as JourneyAction<'webhook'>)
        : undefined;
    const parentId =
      action.action === 'journey_start' ? journeyTriggersId : action.parentAction?.shortId;
    nodes.push({
      id: action.shortId,
      type: action.action,
      hidden: nodesToHide.has(action.shortId),
      parentId,
      ...(action.parentAction && {
        dependantIds: action.dependants,
      }),
      data: {
        values,
        description: action.description,
        nodeDescription,
        isAutomatic: action.isAutomatic,
        assignees: action.assignees,
        isConfigured: action.isConfigured,
        reminder: action.reminder,
        integration,
        isCollapsed: collapsedActions.includes(action.shortId),
        descendantCount: descendantsCountMap[action.shortId],
        childrenCount: childrenMap[action.shortId].length,
        isRemoveDisabled: getIsRemoveDisabled(action),
      },
    });
  });

  return nodes;
};

function clearSearchParam(key: string) {
  const url = new URL(window.location.href);
  url.searchParams.delete(key);
  window.history.replaceState({}, '', url.toString());
}

const JourneyFlow = ({
  journey,
  integrations,
  webhooks,
}: JourneyProps & Pick<ConvertToNodesProps, 'integrations' | 'webhooks'>) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedNode, setNodes, getValidNodes, getNode } = useNodes();
  const [isDragging, setIsDragging] = useState(false);
  const [shortIdToDescriptiveMap, setShortIdToDescriptiveMap] = useState({});
  const { t } = useScopedTranslate(locale);
  const showConfirmModal = useConfirmModal('LARGE');
  const moveMutation = useMoveJourneyAction();
  useRotatingLoadingMessages({
    isEnabled: moveMutation.isLoading,
    firstMessage: t('moving'),
  });

  const { showError } = useToaster();
  const isFirstFetch = useRef(true);
  const collapseNodesFunctions = useCollapsedActions();
  const showAddTravellersSidebar = useAddTravellersSidebar();
  const showEditJourneyFormOverlay = useEditJourneyFormOverlay();
  const showEditJourneyActionFormOverlay = useEditJourneyActionFormOverlay();
  const formsQuery = useJourneyFormsList(journey.shortId);
  const webhooksQuery = useJourneyWebhooksList(journey.shortId);
  const pollingConfigsQuery = useJourneyPollingConfigsList(journey.shortId);

  const triggerForms = formsQuery.data ?? [];
  const triggerWebhooks = webhooksQuery.data ?? [];
  const triggerPollingConfigs = pollingConfigsQuery.data ?? [];
  const triggersCount =
    triggerForms.length + triggerWebhooks.length + triggerPollingConfigs.length + 1;
  const { isCollapsed: isTriggersCollapsed } = useTriggersCollapsedStore();

  const journeyActionFormsQuery = useJourneyActionForms({ journeyId: journey.shortId });
  const journeyActionForms = (journeyActionFormsQuery.data ?? []).reduce((acc, form) => {
    if (form.journeyActionId) {
      acc[form.journeyActionId] = { shortId: form.shortId, name: form.name };
    }
    return acc;
  }, {} as journeyActionFormContextMapType);

  const journeyActionsQuery = useJourneyActions(journey.shortId, {
    onSuccess: (data) => {
      if (data.length > 0) {
        updateNodes(data);

        if (isFirstFetch.current) {
          isFirstFetch.current = false;

          if (searchParams.get('openTriggers')) {
            clearSearchParam('openTriggers');
            showAddTravellersSidebar({
              journeyShortId: journey.shortId,
            });
            return;
          }

          const journeyFormId = searchParams.get('openJourneyForm');
          if (journeyFormId) {
            clearSearchParam('openJourneyForm');
            setTimeout(() => {
              showEditJourneyFormOverlay({
                journeyId: journey.shortId,
                formId: journeyFormId,
              });
            }, 250);
            return;
          }

          const journeyActionFormId = searchParams.get('openJourneyActionForm');
          if (journeyActionFormId) {
            clearSearchParam('openJourneyActionForm');
            setTimeout(() => {
              showEditJourneyActionFormOverlay({
                formId: journeyActionFormId,
                openPreview: true,
              });
            }, 250);
            return;
          }

          if (data.length === 1) {
            const source = data[0].shortId;
            const target = hiddenIdentifier;
            const id = generateEdgeId(source, target);
            const props = { journeyId: journey.shortId, edge: { source, target, id } };
            showCreateActionSidebar(props);
          }
        }
      }
    },
  });
  const availableMovesQuery = useJourneyActionAvailableMoves(journey.shortId, {
    enabled: journeyActionsQuery.isSuccess,
  });

  const validNodes = getValidNodes();

  useEffect(() => {
    // If the query parameter ?focusedAction=shortid is present,
    // we remove it from the parameters and open the sidebar
    // for that action.
    const focusedAction = searchParams.get('focusedAction');
    if (!focusedAction) return;

    const node = getNode(focusedAction);
    if (!node) return;
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('focusedAction');
    setSearchParams(newParams);

    setTimeout(() => {
      showActionSidebar(node);
    }, 200);
  }, [validNodes.length, getNode, searchParams, setSearchParams]);

  useEffect(() => {
    if (journeyActionsQuery.data?.length) {
      updateNodes(journeyActionsQuery.data);
    }
    //eslint-disable-next-line
  }, [journey.collapsedActions.join('-'), triggersCount, isTriggersCollapsed]);

  const updateNodes = (actions: Array<JourneyAction>) => {
    const descriptiveMap = getShortIdToDescriptiveMap(actions);
    setShortIdToDescriptiveMap(descriptiveMap);
    setNodes(
      convertToNodes({
        actions,
        integrations,
        webhooks,
        collapsedActions: journey.collapsedActions,
        triggersCount,
        isTriggersCollapsed,
      })
    );
  };

  const availableMoves = useMemo(() => {
    return availableMovesQuery.data ?? {};
  }, [availableMovesQuery.data]);

  const handleDragDrop = async (event: DragEndEvent) => {
    setIsDragging(false);
    if (event.over) {
      const actionId = event.active.data.current?.id ?? '';
      const edgeId = event.over.id as string;
      const [, parentId, secondId] = edgeId.split('-');
      const childId = edgeId.endsWith(hiddenIdentifier) ? undefined : secondId;
      let dependants: [string, string][] = [];
      try {
        dependants = await listBreakingReferencesByMove({
          actionId,
          parentId,
          childId,
        });
      } catch (e) {
        showError(t('core.errors.generic'));
      }
      const dependantsCount = dependants.length ?? 0;
      const moveData = { actionId, parentId, childId, journeyId: journey.shortId };
      if (dependantsCount > 0) {
        const nodes = getValidNodes();
        const confirmationNodes = dependants?.map(([parentNodeId, childNodeId]) => {
          const parentNode = nodes.find((node) => node.id === parentNodeId);
          const childNode = nodes.find((node) => node.id === childNodeId);
          return [parentNode, childNode];
        }) as [JourneyNode<BASE_ACTION_TYPE>, JourneyNode<BASE_ACTION_TYPE>][];
        showConfirmModal({
          title: t('confirmTitle'),
          headerIcon: <IconFormIndicatorWarning />,
          description: t('confirmDescription'),
          buttonText: t('confirmButton'),
          buttonIcon: <IconMove />,
          buttonVariant: 'primary-blue',
          onConfirm: () => moveMutation.mutate(moveData),
          children: (
            <Stack space={2}>
              <Text font="semibold14">{t('confirmActionLabel')}</Text>

              {confirmationNodes.map(([parentNode, childNode]) => (
                <Stack
                  key={`${parentNode.id}-${childNode.id}`}
                  orientation="horizontal"
                  justify="space-between"
                  align="center"
                  space={1}
                >
                  <GenericActionCard action={nodeToActionBasic(childNode)} />
                  <IconTextFormatLinkOff color="elementRed100" />
                  <GenericActionCard action={nodeToActionBasic(parentNode)} />
                </Stack>
              ))}
            </Stack>
          ),
        });
      } else {
        moveMutation.mutate(moveData);
      }
    }
  };

  const isMoveDisabled = Boolean(selectedNode);

  const moveContextValue = useMemo(() => {
    return {
      availableMoves,
      isMoveDisabled,
    };
  }, [availableMoves, isMoveDisabled]);

  if (journeyActionsQuery.isError) {
    return (
      <ErrorScreen code={500} heading={t('actionsError')} message={t('actionsErrorMessage')} />
    );
  }

  return (
    <>
      <JourneyContext.Provider
        value={{
          journeyActionForms,
          journeyForms: triggerForms,
          webhooks: triggerWebhooks,
          pollingConfigs: triggerPollingConfigs,
          triggersCount,
          journeyId: journey.shortId,
        }}
      >
        <TestIdContext.Provider value={shortIdToDescriptiveMap}>
          <MoveContext.Provider value={moveContextValue}>
            <CollapseContext.Provider value={collapseNodesFunctions}>
              <DndContext
                onDragStart={() => {
                  hideSidebar();
                  setIsDragging(true);
                }}
                onDragEnd={handleDragDrop}
              >
                <StyledBuilder journey={journey} isDragging={isDragging} />
              </DndContext>
            </CollapseContext.Provider>
          </MoveContext.Provider>
        </TestIdContext.Provider>
      </JourneyContext.Provider>
    </>
  );
};

const StyledBuilder = styled(JourneyBuilder)({
  // Needed to display dragging placeholder over nodes
  '.react-flow__node:focus-within': {
    zIndex: '1 !important',
  },
});

const locale = {
  en: {
    fields: 'Fields',
    journey: 'Journey',
    attributes: 'Attributes',
    journeys: 'Journeys',
    publish: 'Publish',
    publishChanges: 'Publish changes',
    cancelChanges: 'Cancel changes',
    published: 'Published',
    draft: 'Draft',
    issues: 'Issues • {num_issues}',
    noIssues: 'No issues',
    journeyNotConfigured:
      'Journey not configured, are there any empty actions or unconfigured actions?',
    newJourney: 'New journey',
    newContent: 'What wonders are in store for this lucky traveller?',
    addFirstAction: 'Add first action',
    journeyNotFound: 'Journey not found',
    deleteJourney: 'Delete',
    configureAction: 'Configure action',
    noDescription: 'No description',
    descriptionBefore: 'Trigger {daysBefore} days before {referenceDate} at {time}',
    descriptionAfter: 'Trigger {daysAfter} days after {referenceDate} at {time}',
    descriptionExact: 'Trigger on {referenceDate} at {time}',
    editJourneyDetails: 'Edit journey details',
    journeyTitle: 'Journey title',
    defaultTitle: 'Untitled journey',
    confirmTitle: 'Warning',
    confirmDescription:
      'Moving this action will break connected values between the actions below.\n\nAre you sure you want to move this action?',
    confirmButton: 'Move action',
    confirmActionLabel: 'Connected actions',
    settings: 'Settings',
    actionsError: 'Unexpected error',
    actionsErrorMessage: 'Failed to load journey builder',
    journeyTriggers: 'Journey triggers',
    publishing: 'Publishing...',
    moving: 'Moving...',
    publishChangesDescription:
      'This Journey has unpublished changes, would you like to publish them?',
    publishJourney: 'Publish Journey',
    dontPublish: "Don't publish",
    dontShowAgain: 'Don’t show again for this Journey',
    keepAsIs: 'Keep as is',
  },
};
